import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Storage as StorageIcon,
  AccessTime as AccessTimeIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { statsApi } from '../services/api';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  subtitle?: string;
}

function StatCard({ title, value, icon, color = 'primary', subtitle }: StatCardProps) {
  const getColor = (color: string) => {
    const colors = {
      primary: '#1976d2',
      secondary: '#dc004e',
      success: '#2e7d32',
      warning: '#ed6c02',
      error: '#d32f2f',
      info: '#0288d1',
    };
    return colors[color as keyof typeof colors] || colors.primary;
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: getColor(color) + '15',
              color: getColor(color),
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" component="div" sx={{ mb: 1, fontWeight: 600 }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function FileMetrics() {
  const [includeBlocked, setIncludeBlocked] = useState(false);
  const [days, setDays] = useState<number | null>(null); // null = all time

  const { data, isLoading, error } = useQuery({
    queryKey: ['fileMetrics', days, includeBlocked],
    queryFn: () => statsApi.getFileMetrics(days, includeBlocked),
    refetchInterval: 60000, // Обновляем каждую минуту
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Ошибка загрузки файловых метрик: {(error as Error).message}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Нет данных для отображения
      </Alert>
    );
  }

  const { file_sizes, audio_duration, context } = data;

  // Format file size for display (choose GB or MB based on size)
  const formatFileSize = (mb: number) => {
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} ГБ`;
    }
    return `${mb.toFixed(2)} МБ`;
  };

  return (
    <Box>
      {/* Period and Filter Selectors */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        {/* Blocked Users Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={includeBlocked}
              onChange={(e) => setIncludeBlocked(e.target.checked)}
              color="primary"
            />
          }
          label="Считать заблокированных пользователей"
        />

        {/* Period Selector */}
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Период</InputLabel>
          <Select
            value={days === null ? 'all' : days}
            label="Период"
            onChange={(e) => {
              const value = e.target.value;
              setDays(value === 'all' ? null : (value as number));
            }}
          >
            <MenuItem value="all">Всё время</MenuItem>
            <MenuItem value={1}>Сегодня</MenuItem>
            <MenuItem value={7}>7 дней</MenuItem>
            <MenuItem value={30}>30 дней</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Main Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Общий объём"
            value={formatFileSize(file_sizes.total_mb)}
            icon={<StorageIcon />}
            color="primary"
            subtitle={`${file_sizes.files_count} файлов`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Всего часов"
            value={audio_duration.total_hours.toFixed(1)}
            icon={<AccessTimeIcon />}
            color="success"
            subtitle={`${audio_duration.audio_count} записей`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Средний размер"
            value={formatFileSize(file_sizes.average_per_file_mb)}
            icon={<InsertDriveFileIcon />}
            color="info"
            subtitle="На файл"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Средняя длительность"
            value={`${audio_duration.average_length_minutes.toFixed(1)} мин`}
            icon={<ScheduleIcon />}
            color="warning"
            subtitle="На запись"
          />
        </Grid>
      </Grid>

      {/* Detailed Info Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <StorageIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Статистика по размерам файлов
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Общий объём
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="primary.main">
                    {formatFileSize(file_sizes.total_mb)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Количество файлов
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {file_sizes.files_count.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Средний размер файла
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="info.main">
                    {formatFileSize(file_sizes.average_per_file_mb)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    На пользователя
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="secondary.main">
                    {formatFileSize(file_sizes.average_per_user_mb)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Активных пользователей с файлами
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {context.active_users_with_files}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <AccessTimeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Статистика по длительности аудио
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Всего часов
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    {audio_duration.total_hours.toFixed(2)} ч
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Всего минут
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {audio_duration.total_minutes.toFixed(0)} мин
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Средняя длительность записи
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="warning.main">
                    {audio_duration.average_length_minutes.toFixed(1)} мин
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Минут на пользователя
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="secondary.main">
                    {audio_duration.average_minutes_per_user.toFixed(1)} мин
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Минут в день (в среднем)
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="info.main">
                    {audio_duration.average_minutes_per_day.toFixed(1)} мин
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Активных пользователей с аудио
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {context.active_users_with_audio}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
